import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { FaWeight, FaCheckCircle, FaInfoCircle, FaArrowRight } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { goReserve } from '../../utils/navigation';
import NoticeContent from '../../components/NoticeContent';
import { useMedicinePrices, toSinglePriceItems } from '../../hooks/useMedicinePrices';

interface ObesityFemaleProps {
  ipAllowed: boolean;
}

/** 注意事項のバージョン（文面更新時に更新。既存同意は失効し再チェックが必要） */
const NOTICE_VERSION = 'v2025-10-01';

const ObesityFemale: React.FC<ObesityFemaleProps> = ({ ipAllowed }) => {
  const { t, i18n } = useTranslation();
  const { isAuthenticated } = useAuth();
  const lang: 'ja' | 'zh' = i18n.language.toLowerCase().startsWith('zh') ? 'zh' : 'ja';
  const navigate = useNavigate();

  const { medicines } = useMedicinePrices('obesity_female', lang);

  // ✅ 全量文案（中/日）
  const L = {
    ja: {
      helmetTitle: 'メディカルダイエット・女性 - Melodis Clinic',
      pageTitle: 'メディカルダイエット・女性',
      description:
        'メディカルダイエットでは医薬品を用いて体重減量を行います。基本は食欲を抑え摂取カロリーを減らすこと、体質や目標に合わせて処方を最適化します。オンライン診療で生活状況や既往歴を伺い、無理のない継続をサポートします。',
      sectionTitle: 'ご案内',
      badges: ['診察料 0円', '24時間オンライン予約', 'スピード発送', '自由診療（保険適用外）'],
      // Drug cards
      cards: {
        rybelsusTitle: '① リベルサス（経口GLP-1受容体作動薬）',
        rybelsusText:
          '内服で食欲を抑制。まずは低用量から開始し、約1か月の反応をみて増量を検討します。',
        mounjaroTitle: '② マンジャロ（GIP/GLP-1受容体作動薬：注射）',
        mounjaroText:
          '有効成分はテルゼパチド。週1回の自己注射で食欲を抑え、熱産生や脂肪分解を促進し基礎代謝の向上が期待できます。オンラインで自己注射の指導も行います。',
        farxigaTitle: '③ フォシーガ（SGLT2阻害薬）',
        farxigaText:
          '腎臓での糖再吸収を抑え尿からブドウ糖を排出します。1錠で約200〜500kcal相当を排出するとされ、まず5mgから開始、体調と体重の推移に合わせて増量します。',
        metforminTitle: '④ メトホルミン',
        metforminText:
          'インスリン抵抗性を改善し、血糖コントロール・食欲抑制・脂肪蓄積抑制を通じて減量を後押しします。',
      },
      priceTitle: '価格（診察料 0円）：',
      // ▼ 男性ページと同一の価格・表記に更新
      priceItems: [
        { name: 'マンジャロ (Mounjaro)', dosage: '2.5mg 4本(1か月)', price: 13000, taxPrice: 14300 },
        { name: 'マンジャロ (Mounjaro)', dosage: '5mg 4本(1か月)', price: 26000, taxPrice: 28600 },
        { name: 'マンジャロ (Mounjaro)', dosage: '7.5mg 4本(1か月)', price: 39000, taxPrice: 42900 },
        { name: 'マンジャロ (Mounjaro)', dosage: '10mg 4本(1か月)', price: 55000, taxPrice: 60500 },
        { name: 'リベルサス (Rybelsus)', dosage: '3mg 30錠(1か月)', price: 6100, taxPrice: 6710 },
        { name: 'リベルサス (Rybelsus)', dosage: '7mg 30錠(1か月)', price: 13800, taxPrice: 15180 },
        { name: 'リベルサス (Rybelsus)', dosage: '14mg 30錠(1か月)', price: 21800, taxPrice: 23980 },
        { name: 'フォシーガ (Forxiga)', dosage: '5mg 30錠(1か月)', price: 9800, taxPrice: 10780 },
        { name: 'フォシーガ (Forxiga)', dosage: '10mg 30錠(1か月)', price: 16800, taxPrice: 18480 },
      ],
      priceNote:
        '※ 服用中の薬・既往歴・心腎機能などにより処方できない場合があります。医師の判断に従ってください。',
      faqBtn: 'FAQを見る',
      noticeBtn: '注意事項はこちらから',
      treatmentTitle: '治療の流れ・当院の特徴',
      treatments: [
        'オンライン診療で症状・既往歴・内服薬を確認',
        '医師が薬の種類・用量を提案',
        '最短次日から内服開始が可能',
        '副作用や併用禁忌を丁寧にご説明',
      ],
      recommendTitle: 'こんな方におすすめ',
      recommends: [
        '食欲が強く食事量の調整が難しい',
        '忙しくて通院の時間が取りづらい',
        'まずは内服から始めたい／注射も検討したい',
        '既往歴や併用薬に配慮した提案を受けたい',
      ],
      cta: 'オンライン診療を予約する',
      modalTitle: 'メディカルダイエットの注意事項',
      modalAgree: '上記を読み、内容に同意します。',
      modalCta: '同意して予約に進む',
      modalClose: '閉じる',
    },
    zh: {
      helmetTitle: '医疗减重・女性 - Melodis Clinic',
      pageTitle: '医疗减重・女性',
      description:
        '通过规范用药辅助减重。以“抑制食欲、降低摄入”为核心，结合个体体质与目标优化处方。我们通过在线问诊了解生活状况与既往史，帮助你更轻松、可持续地坚持。',
      sectionTitle: '就诊说明',
      badges: ['诊察费 0 日元', '24 小时在线预约', '急速发货', '自费诊疗（不适用医保）'],
      cards: {
        rybelsusTitle: '① 口服司美格鲁肽 (Rybelsus)',
        rybelsusText:
          '口服抑制食欲。通常从低剂量开始，约 1 个月评估疗效与不良反应后再考虑加量。',
        mounjaroTitle: '② 替尔泊肽注射液 (Mounjaro)',
        mounjaroText:
          '每周自我注射 1 次，可抑制食欲并促进产热与脂肪分解，有望提升基础代谢；我们提供在线注射指导。',
        farxigaTitle: '③ 达格列净片 (Farxiga)',
        farxigaText:
          '抑制肾脏对葡萄糖的重吸收，使糖分经尿液排出；据估算每日可相当于排出约 200–500 kcal 的糖分。通常从 5mg 起，根据状态与体重调整。',
        metforminTitle: '④ 二甲双胍 (Metformin)',
        metforminText:
          '改善胰岛素抵抗；通过血糖控制、抑制食欲与减少脂肪堆积，辅助体重下降。',
      },
      priceTitle: '价格（诊察费 0 日元）：',
      priceItems: [
        { name: '替尔泊肽注射液 (Mounjaro)', dosage: '2.5mg 4支(1个月)', price: 13000, taxPrice: 14300 },
        { name: '替尔泊肽注射液 (Mounjaro)', dosage: '5mg 4支(1个月)', price: 26000, taxPrice: 28600 },
        { name: '替尔泊肽注射液 (Mounjaro)', dosage: '7.5mg 4支(1个月)', price: 39000, taxPrice: 42900 },
        { name: '替尔泊肽注射液 (Mounjaro)', dosage: '10mg 4支(1个月)', price: 55000, taxPrice: 60500 },
        { name: '口服司美格鲁肽 (Rybelsus)', dosage: '3mg 30片(1个月)', price: 6100, taxPrice: 6710 },
        { name: '口服司美格鲁肽 (Rybelsus)', dosage: '7mg 30片(1个月)', price: 13800, taxPrice: 15180 },
        { name: '口服司美格鲁肽 (Rybelsus)', dosage: '14mg 30片(1个月)', price: 21800, taxPrice: 23980 },
        { name: '达格列净片 (Forxiga)', dosage: '5mg 30片(1个月)', price: 9800, taxPrice: 10780 },
        { name: '达格列净片 (Forxiga)', dosage: '10mg 30片(1个月)', price: 16800, taxPrice: 18480 },
      ],
      priceNote:
        '※ 若合并用药/既往史/心肾功能异常等，可能不适合处方；请遵医嘱执行。',
      faqBtn: '查看 FAQ',
      noticeBtn: '查看注意事项',
      treatmentTitle: '就诊流程与本院特点',
      treatments: [
        '在线问诊确认症状/既往史/在用药',
        '医生推荐合适的药物与剂量',
        '最快次日即可开始服药',
        '详细说明不良反应与禁忌合用',
      ],
      recommendTitle: '适合人群',
      recommends: [
        '食欲旺盛、难以自行控制饮食',
        '日程繁忙、不便频繁到院',
        '想先从口服开始／也考虑注射',
        '希望在既往史与合并用药下获得更安全的方案',
      ],
      cta: '立即预约在线咨询',
      modalTitle: '医疗减重的注意事项',
      modalAgree: '我已阅读并同意上述内容。',
      modalCta: '同意并继续预约',
      modalClose: '关闭',
    },
  } as const;

  const treatments = L[lang].treatments;
  const recommends = L[lang].recommends;

  // 注意事項モーダル
  const [noticeOpen, setNoticeOpen] = useState(false);
  const [agree, setAgree] = useState(false);

  // モーダル表示中は body スクロールロック + ESC で閉じる
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setNoticeOpen(false);
    if (noticeOpen) {
      window.addEventListener('keydown', onKey);
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        window.removeEventListener('keydown', onKey);
        document.body.style.overflow = prev;
      };
    }
  }, [noticeOpen]);

  const openNotice = () => {
    setAgree(false);
    setNoticeOpen(true);
  };

  const proceedReserve = () => {
    if (!agree) return;
    setNoticeOpen(false);
    // 予約ページへ：科目をプリセット＋注意事項同意済み＋紹介ページ由来
    goReserve(navigate, ipAllowed, {
      preselectService: 'obesity_female',
      preAgreed: true,
      state: { from: 'service' },
    });
  };

  // 価格表は DB(admin設定)優先。ただし「区分(セクション)単位」でフォールバックする:
  // 該当区分にDB薬が1件でもあればDB価格、無ければその区分のハードコード現行価を維持。
  // → 生産で一部の薬だけ登録しても、未登録区分が空にならず現行価が消えない(逐次移行が安全)。
  const dbItems = toSinglePriceItems(medicines, lang);
  const hcItems: any[] = [...L[lang].priceItems];
  const section = (pred: (x: any) => boolean): any[] => {
    const d = dbItems.filter(pred);
    return d.length > 0 ? d : hcItems.filter(pred);
  };

  return (
    <div className="pt-0 sm:pt-10 pb-10 sm:pb-20 bg-[#fff9f8] font-sans" translate="no">
      <Helmet>
        <title>{t('services.obesity_female.title')} - Melodis Clinic</title>
        <meta name="description" content={t('seo.obesity_female.description')} />
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Product',
            name: lang === 'ja' ? 'マンジャロ (Manjaro) 2.5mg/5mg/7.5mg/10mg' : '替尔泊肽注射液 (Mounjaro) 2.5mg/5mg/7.5mg/10mg',
            image: 'https://melodis-clinic.com/images/15.png',
            description: lang === 'ja'
              ? 'マンジェロはGLP-1とGIPの2つの受容体に作用する最新の医療ダイエット薬です。自宅からオンライン診療で処方が可能です。'
              : '替尔泊肽注射液 (Mounjaro) 是同时作用于 GLP-1 和 GIP 受体的医疗减重药物。支持线上问诊与处方。',
            brand: {
              '@type': 'Brand',
              name: lang === 'ja' ? '日本イーライリリー / 田辺三菱製薬' : '日本礼来 / 田边三菱制药'
            },
            offers: {
              '@type': 'Offer',
              url: window.location.href,
              priceCurrency: 'JPY',
              price: '20800',
              availability: 'https://schema.org/InStock',
              seller: {
                '@type': 'MedicalOrganization',
                name: 'Melodis Clinic',
                telephone: '+81-3-6908-6228',
                address: {
                  '@type': 'PostalAddress',
                  streetAddress: '3-23-3 Higashi-Ikebukuro, 804',
                  addressLocality: 'Toshima-ku',
                  addressRegion: 'Tokyo',
                  postalCode: '170-0013',
                  addressCountry: 'JP'
                }
              }
            }
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: [
              {
                '@type': 'Question',
                name: lang === 'ja' ? 'マンジャロとウゴービの違いは何ですか？' : '替尔泊肽 (Mounjaro) 和 诺和盈（Wegovy）有什么区别？',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: lang === 'ja'
                    ? 'マンジャロは2つの受容体に作用し、ウゴービより高い体重減少効果が報告されています。どちらも医療ダイエットとして非常に効果的です。'
                    : '替尔泊肽 (Mounjaro) 作用于两个受体，研究表明其减重效果优于诺和盈。两者在医疗减重方面都非常有效。'
                }
              }
            ]
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'MedicalWebPage',
            potentialAction: {
              '@type': 'ReserveAction',
              target: {
                '@type': 'EntryPoint',
                urlTemplate: 'https://melodis-clinic.com/reserve',
                actionPlatform: [
                  'http://schema.org/DesktopWebPlatform',
                  'http://schema.org/MobileWebPlatform'
                ]
              }
            }
          })}
        </script>
      </Helmet>

      <div className="container mx-auto px-0 sm:px-4 max-w-5xl">
        <div className="bg-white shadow-none sm:shadow-md rounded-none sm:rounded-3xl p-5 sm:p-8 md:p-12 border-y sm:border border-slate-100">
          {/* Header */}
          <div className="flex items-center mb-6">
            <div className="mr-4 rounded-2xl bg-rose-100 p-3">
              <FaWeight className="text-rose-600 w-7 h-7" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-slate-800">
              <span className="text-emerald-400 mr-2">│</span>{L[lang].pageTitle}
            </h1>
          </div>

          <div className="mb-8 flex items-center gap-3 text-left">
            <Link
              to="/about"
              className="inline-flex items-center gap-2 rounded-full bg-slate-50 border border-slate-200 px-4 py-1.5 text-xs md:text-sm text-slate-600 hover:bg-slate-100 transition"
            >
              <span className="w-2 h-2 rounded-full bg-emerald-400" />
              {t('seo.supervision_doctor')}
              <span className="text-[10px] text-slate-400 font-normal ml-1">{t('seo.supervision_title')}</span>
            </Link>
          </div>

          <p className="text-slate-700 text-base md:text-lg leading-relaxed mb-8 bg-[#fff9f8]/50 border-l-4 border-sky-200 pl-4 py-2">
            {t('seo.obesity_female.summary')}
          </p>

          {/* Short description */}
          <p className="text-slate-700 text-base md:text-lg leading-relaxed mb-8">
            {L[lang].description}
          </p>

          {/* 詳しいご案内 */}
          <section className="rounded-2xl border border-rose-100 bg-rose-50/60 p-6 md:p-8 mb-8">
            <div className="flex items-center gap-2 mb-4">
              <FaInfoCircle className="text-rose-600 w-5 h-5" />
              <h2 className="text-2xl font-semibold text-rose-700">{L[lang].sectionTitle}</h2>
            </div>

            {/* badges */}
            <ul className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6 text-sm">
              {L[lang].badges.map((b, i) => (
                <li
                  key={i}
                  className="rounded-xl border border-rose-200 bg-white px-4 py-3 text-slate-800 text-center"
                >
                  {b}
                </li>
              ))}
            </ul>

            <div className="space-y-4 text-slate-800 leading-relaxed">
              <p>
                {lang === 'ja'
                  ? 'メディカルダイエットでは医薬品を用いて体重減量を行います。基本は強い我慢ではなく、食欲を適切にコントロールしながら無理のない継続を目指します。'
                  : '医疗减重并非“靠意志力硬扛”，而是通过规范用药合理控制食欲，更容易、可持续地达成目标。'}
              </p>
            </div>

            {/* Drug cards */}
            <div className="grid md:grid-cols-2 gap-4 mt-6">
              <div className="rounded-xl border border-slate-200 bg-white p-5">
                <h3 className="text-lg font-semibold text-slate-800 mb-1">{L[lang].cards.rybelsusTitle}</h3>
                <p className="text-slate-700">{L[lang].cards.rybelsusText}</p>
              </div>

              <div className="rounded-xl border border-slate-200 bg-white p-5">
                <h3 className="text-lg font-semibold text-slate-800 mb-1">{L[lang].cards.mounjaroTitle}</h3>
                <p className="text-slate-700">{L[lang].cards.mounjaroText}</p>
              </div>

              <div className="rounded-xl border border-slate-200 bg-white p-5">
                <h3 className="text-lg font-semibold text-slate-800 mb-1">{L[lang].cards.farxigaTitle}</h3>
                <p className="text-slate-700">{L[lang].cards.farxigaText}</p>
              </div>

              {/* <div className="rounded-xl border border-slate-200 bg-white p-5">
                <h3 className="text-lg font-semibold text-slate-800 mb-1">{L[lang].cards.metforminTitle}</h3>
                <p className="text-slate-700">{L[lang].cards.metforminText}</p>
              </div> */}
            </div>

            {/* Prices */}
            <div className="mt-6 rounded-xl border border-slate-200 bg-white p-5 md:p-8 text-left">
              <h3 className="text-xl font-semibold text-slate-800 mb-6">{L[lang].priceTitle}</h3>
              
              <div className="space-y-8">
                <div>
                  <h4 className="font-bold text-rose-700 mb-3 border-b border-rose-100 pb-1">
                    {lang === 'ja' ? '注射薬 (GLP-1/GIP)' : '注射药 (GLP-1/GIP)'}
                  </h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    {section((item: any) => typeof item === 'object' && /マンジャロ|Mounjaro|替尔泊肽/.test(item.name)).map((item: any, i: number) => (
                      <div key={i} className="rounded-xl bg-rose-50/50 p-4 border border-rose-100 flex flex-col">
                        <div className="flex justify-between items-start mb-2">
                          <span className="font-bold text-slate-800">{item.name}</span>
                        </div>
                        <div className="text-xs text-slate-500 mb-3">{item.dosage}</div>
                        <div className="mt-auto text-right">
                          <div className="flex items-baseline justify-end gap-1">
                            <span className="font-bold text-lg text-slate-900">{item.price.toLocaleString()}</span>
                            <span className="text-sm text-slate-600">{lang === 'ja' ? '円' : ' 日元'}</span>
                          </div>
                          <div className="text-[11px] text-slate-400 font-normal">
                            ({lang === 'ja' ? '税込' : '含税'} {item.taxPrice.toLocaleString()}{lang === 'ja' ? '円' : ' 日元'})
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="font-bold text-rose-700 mb-3 border-b border-rose-100 pb-1">
                    {lang === 'ja' ? '内服薬 (GLP-1 / SGLT2)' : '口服药 (GLP-1 / SGLT2)'}
                  </h4>
                  <ul className="space-y-3 text-slate-700">
                    {section((item: any) => typeof item === 'object' && !/マンジャロ|Mounjaro|替尔泊肽/.test(item.name)).map((item: any, i: number) => (
                      <li key={i} className="flex justify-between items-start flex-wrap gap-2 py-1">
                        <span className="flex flex-col text-left">
                          <span className="font-bold text-slate-800">{item.name}</span>
                          <span className="text-[11px] text-slate-500">{item.dosage}</span>
                        </span>
                        <div className="text-right">
                          <div className="flex items-baseline gap-1 justify-end">
                            <span className="font-bold text-slate-900">{item.price.toLocaleString()}</span>
                            <span className="text-sm text-slate-600">{lang === 'ja' ? '円' : ' 日元'}</span>
                          </div>
                          <div className="text-[10px] text-slate-400 font-normal">
                            ({lang === 'ja' ? '税込' : '含税'} {item.taxPrice.toLocaleString()}{lang === 'ja' ? '円' : ' 日元'})
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* links */}
              <div className="mt-8 flex flex-wrap gap-3">
                <button
                  onClick={() => navigate('/faq/diet-female')}
                  className="inline-flex items-center gap-2 rounded-full border border-sky-300 bg-[#fff9f8] px-6 py-2.5 text-sm font-medium text-sky-700 hover:bg-sky-100 transition"
                >
                  {L[lang].faqBtn} <FaArrowRight className="w-4 h-4" />
                </button>
                <button
                  onClick={openNotice}
                  className="inline-flex items-center gap-2 rounded-full border border-rose-300 bg-rose-50 px-6 py-2.5 text-sm font-medium text-rose-700 hover:bg-rose-100 transition"
                >
                  {L[lang].noticeBtn} <FaArrowRight className="w-4 h-4" />
                </button>
              </div>

              <p className="mt-4 text-slate-500 text-xs">{L[lang].priceNote}</p>
            </div>
          </section>

          {/* Treatment list */}
          <div className="rounded-2xl p-6 md:p-8 mb-8 border border-rose-100 bg-rose-50/60">
            <h2 className="text-2xl font-semibold text-rose-700 mb-4">{L[lang].treatmentTitle}</h2>
            <div className="flex flex-wrap gap-3">
              {L[lang].treatments.map((item, idx) => (
                <span
                  key={idx}
                  className="inline-flex items-center gap-2 rounded-full border border-rose-200 bg-white px-4 py-2 text-sm text-slate-800"
                >
                  <FaCheckCircle className="text-rose-500 w-4 h-4" />
                  {item}
                </span>
              ))}
            </div>
          </div>

          {/* Recommend list */}
          <div className="rounded-2xl p-6 md:p-8 bg-slate-50 border border-slate-100">
            <h3 className="text-xl font-semibold text-slate-800 mb-4">{L[lang].recommendTitle}</h3>
            <ul className="grid md:grid-cols-2 gap-3">
              {L[lang].recommends.map((item, idx) => (
                <li
                  key={idx}
                  className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 text-slate-700"
                >
                  <FaCheckCircle className="text-rose-500 w-5 h-5 mt-0.5" />
                  {item}
                </li>
              ))}
            </ul>
          </div>

          {/* CTA（→ モーダル経由） */}
          {isAuthenticated && (
            <div className="mt-10 text-center">
              <button
                onClick={openNotice}
                className="inline-block bg-rose-500 text-white px-10 py-3.5 text-base md:text-lg rounded-full font-semibold shadow-md hover:bg-rose-600 transition active:scale-95"
              >
                {L[lang].cta}
              </button>
              <div className="mx-auto mt-3 h-1 w-20 rounded-full bg-sky-300/60" />
            </div>
          )}
        </div>
      </div>

      {/* 注意事項モーダル（public/data/notices から読込） */}
      {noticeOpen && (
        <div
          className="fixed inset-0 z-[1000] overflow-y-auto"
          role="dialog"
          aria-modal="true"
          aria-labelledby="notice-title"
          onClick={() => setNoticeOpen(false)}
        >
          {/* overlay */}
          <div className="fixed inset-0 bg-black/40" />

          {/* content wrapper */}
          <div
            className="min-h-full flex items-start md:items-center justify-center p-4 md:p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative z-[1001] w-full max-w-2xl bg-white rounded-2xl shadow-xl p-6 md:p-7 border border-slate-200 max-h-[90vh] overflow-y-auto">
              <div className="flex items-start justify-between gap-4 mb-3">
                <h3 id="notice-title" className="text-xl md:text-2xl font-semibold text-slate-900">
                  {L[lang].modalTitle}
                </h3>
                <button
                  onClick={() => setNoticeOpen(false)}
                  aria-label="Close"
                  className="rounded-full px-3 py-1.5 bg-slate-100 text-slate-600 hover:bg-slate-200"
                >
                  ×
                </button>
              </div>

              {/* JSON → NoticeContent 経由で描画（言語フォールバック＋キャッシュバスター） */}
              <NoticeContent service="obesity_female" lang={i18n.language} version={NOTICE_VERSION} />

              <label className="mt-4 flex items-start gap-2 text-slate-700">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={agree}
                  onChange={(e) => setAgree(e.target.checked)}
                />
                <span>{L[lang].modalAgree}</span>
              </label>

              <div className="mt-5 flex flex-wrap gap-3">
                <button
                  disabled={!agree}
                  onClick={proceedReserve}
                  className={`inline-flex items-center justify-center rounded-full px-6 py-2.5 font-semibold text-white transition ${agree ? 'bg-rose-500 hover:bg-rose-600 active:bg-rose-700' : 'bg-rose-300 cursor-not-allowed'
                    }`}
                >
                  {L[lang].modalCta}
                </button>
                <button
                  onClick={() => setNoticeOpen(false)}
                  className="rounded-full border border-slate-300 px-6 py-2.5 text-slate-700 hover:bg-slate-50"
                >
                  {L[lang].modalClose}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ObesityFemale;
